import clsx from 'clsx';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useFetcher, useSearchParams } from 'react-router';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { formatDateRange, formatDayWithWeekday, formatTime } from './format';
import ResultsSection from './ResultsSection';
import {
  type Competition,
  EMAIL_PATTERN,
  type LookupResult,
  MOBILE_PATTERN,
  type ParticipantResult,
  type Results,
  STATUS_LABELS,
  TYPE_LABELS,
} from './types';

type Mode = 'mobile' | 'email';

const NOT_YET = 'хараахан гараагүй байна';

const FORMS_ORIGIN = 'https://forms.mn';
const HEIGHT_SPARE = 4;

type Panel = 'register' | 'check';

// Why a panel can't be opened for this status; null when it can.
function unavailableReason(panel: Panel, competition: Competition): string | null {
  switch (competition.status) {
    case 'PLANNED':
      return panel === 'register' ? 'Бүртгэл удахгүй нээгдэнэ' : 'Бүртгэл нээгдсэний дараа';
    case 'REGISTRATION_OPEN':
      return panel === 'register' && !competition.forms_slug
        ? 'Бүртгэлийн форм удахгүй нэмэгдэнэ'
        : null;
    case 'REGISTRATION_CLOSED':
      return panel === 'register' ? 'Бүртгэл хаагдсан' : null;
    case 'FINISHED':
      return 'Тэмцээн дууссан';
    case 'CANCELLED':
      return 'Тэмцээн цуцлагдсан';
  }
}

// The description (who may enter, fees, prizes, rules) is the competition's
// "Удирдамж": closed until opened. tsum sends it as sanitised HTML.
function Description({ html }: { html: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-6 rounded-3xl bg-white shadow-lg">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 p-5 text-left sm:p-6"
      >
        <span className="text-lg font-bold text-green-900">Удирдамж</span>
        <Icon
          icon="chevronDown"
          className={clsx(
            'h-5 w-5 flex-shrink-0 text-green-700 duration-200 ease-in-out',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div
          className="prose max-w-none px-5 pb-6 prose-img:rounded-2xl sm:px-6 [&_audio]:w-full [&_li:has(>input)]:list-none [&_li>input+p]:inline [&_li>input]:mr-2 [&_video]:w-full [&_video]:rounded-2xl"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitised by tsum (competitionService)
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </div>
  );
}

function TipsList({ tips, className }: { tips: string[]; className?: string }) {
  if (!tips.length) return null;
  return (
    <div className={clsx('rounded-3xl bg-orange-100 p-5 sm:p-6', className)}>
      <p className="flex items-center gap-2 font-semibold text-green-900">
        <Icon icon="bulb" className="h-5 w-5 text-orange-600" />
        Зөвлөмж
      </p>
      <ul className="mt-3 space-y-2 text-sm text-green-900">
        {tips.map((tip) => (
          <li key={tip} className="flex items-start gap-2.5">
            <Icon icon="check" className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-600" />
            <span>{tip}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// The forms.mn form as its embed.js would place it: an iframe that reports its
// height by postMessage ({ type: 'forms.mn:height', slug, height }).
function RegisterPanel({ slug }: { slug: string }) {
  const [height, setHeight] = useState(640);
  // Rendered only once the listener is up: an iframe in the server HTML loads
  // before hydration and its first height message would be lost.
  const [listening, setListening] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== FORMS_ORIGIN || e.data?.type !== 'forms.mn:height' || e.data.slug !== slug)
        return;
      // scrollHeight is rounded down; a few spare pixels keep a scrollbar away.
      setHeight(Math.max(200, Number(e.data.height) || 0) + HEIGHT_SPARE);
    };
    window.addEventListener('message', onMessage);
    setListening(true);
    return () => window.removeEventListener('message', onMessage);
  }, [slug]);

  return (
    // Edge to edge on phones: the form's Cloudflare check is 300px wide and
    // scrolls sideways in anything narrower.
    <div className="animate-rise -mx-4 sm:mx-0">
      <div className="relative bg-white px-3 py-4 shadow-xl sm:rounded-3xl sm:px-6 sm:py-6">
        {!loaded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-green-700">
            <span className="h-9 w-9 animate-spin rounded-full border-4 border-green-100 border-t-green-600" />
            <span className="text-sm font-medium">Маягт ачаалж байна…</span>
          </div>
        )}
        {listening ? (
          <iframe
            src={`${FORMS_ORIGIN}/f/${slug}?embed=1`}
            title="Бүртгэлийн форм"
            allow="payment"
            onLoad={() => setLoaded(true)}
            className={clsx(
              'block w-full border-0 duration-300 ease-in-out',
              loaded ? 'opacity-100' : 'opacity-0',
            )}
            style={{ height, minHeight: 420 }}
          />
        ) : (
          <div style={{ height }} />
        )}
      </div>

      <p className="mt-4 px-4 text-center text-sm text-green-800 sm:px-0">
        Төлбөрийн апп нээгдэхгүй байвал{' '}
        <a
          href={`${FORMS_ORIGIN}/f/${slug}`}
          target="_blank"
          rel="noopener"
          className="font-semibold text-green-700 underline underline-offset-2 hover:text-green-600"
        >
          маягтыг шинэ цонхонд нээнэ үү
        </a>
        .
      </p>
    </div>
  );
}

function PanelButton({
  icon,
  title,
  subtitle,
  selected,
  reason,
  onClick,
}: {
  icon: 'send' | 'listCheck';
  title: string;
  subtitle: string;
  selected: boolean;
  reason: string | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      disabled={!!reason}
      onClick={onClick}
      className={clsx(
        'flex items-start gap-4 rounded-3xl border-2 p-5 text-left duration-300 ease-in-out sm:p-6',
        reason
          ? 'cursor-not-allowed border-transparent bg-white bg-opacity-60 opacity-70'
          : selected
            ? 'border-green-600 bg-white shadow-xl'
            : 'border-transparent bg-white shadow-lg hover:-translate-y-1 hover:shadow-xl',
      )}
    >
      <span
        className={clsx(
          'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl',
          selected && !reason ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700',
        )}
      >
        <Icon icon={icon} className="h-6 w-6" />
      </span>
      <span className="min-w-0">
        <span className="block text-lg font-bold text-green-900 sm:text-xl">{title}</span>
        <span className="mt-0.5 block text-sm text-green-800">{reason ?? subtitle}</span>
      </span>
    </button>
  );
}

function ResultCard({
  participant,
  competition,
}: {
  participant: ParticipantResult;
  competition: Competition;
}) {
  const isOlympiad = competition.type === 'OLYMPIAD';
  const name = [participant.last_name, participant.first_name].filter(Boolean).join(' ');
  const time =
    participant.start_at &&
    `${formatTime(participant.start_at)}${participant.end_at ? `–${formatTime(participant.end_at)}` : ''}`;

  return (
    <div className="animate-rise rounded-3xl bg-white p-5 shadow-lg sm:p-7">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          {participant.class_year_name && (
            <p className="text-sm font-medium text-green-600">{participant.class_year_name}</p>
          )}
          <h3 className="mt-1 break-words text-xl font-bold text-green-900 sm:text-2xl">{name}</h3>
          {participant.school_name && (
            <p className="mt-1 text-green-800">{participant.school_name}</p>
          )}
        </div>

        {isOlympiad && (
          <div className="flex-shrink-0 rounded-2xl bg-green-50 text-center sm:w-32">
            <div className="px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-wide text-green-700">Өрөө</p>
              <p className="mt-1 text-2xl font-bold text-green-900">
                {participant.exam_room_number ?? '—'}
              </p>
            </div>
          </div>
        )}
      </div>

      <hr className="my-5 border-green-50" />

      <ul className="grid gap-3 text-sm text-green-800 sm:grid-cols-2">
        <li className="flex items-start gap-2.5">
          <Icon icon="calendar" className="mt-0.5 h-4.5 w-4.5 flex-shrink-0 text-green-600" />
          <span>
            <span className="block text-xs text-green-700">Өдөр</span>
            <span className="font-semibold text-green-900">
              {participant.start_at ? formatDayWithWeekday(participant.start_at) : NOT_YET}
            </span>
          </span>
        </li>
        <li className="flex items-start gap-2.5">
          <Icon icon="clock" className="mt-0.5 h-4.5 w-4.5 flex-shrink-0 text-green-600" />
          <span>
            <span className="block text-xs text-green-700">Цаг</span>
            <span className="font-semibold text-green-900">{time || NOT_YET}</span>
          </span>
        </li>
        {participant.arrival_at && (
          <li className="flex items-start gap-2.5 sm:col-span-2">
            <Icon icon="check" className="mt-0.5 h-4.5 w-4.5 flex-shrink-0 text-yellow-900" />
            <span className="rounded-xl bg-yellow-100 px-3 py-1.5 font-semibold text-green-900">
              {formatTime(participant.arrival_at)}-д бэлэн байх
            </span>
          </li>
        )}
        {competition.location && (
          <li className="flex items-start gap-2.5">
            <Icon icon="mapPin" className="mt-0.5 h-4.5 w-4.5 flex-shrink-0 text-green-600" />
            <span>
              <span className="block text-xs text-green-700">Байршил</span>
              <span className="font-semibold text-green-900">{competition.location}</span>
            </span>
          </li>
        )}
      </ul>
    </div>
  );
}

// Participant lookup (CONTEXT.md): a parent finds where and when their child
// takes part, by the phone number or email they registered with.
function CheckPanel() {
  const fetcher = useFetcher<LookupResult>();
  const [mode, setMode] = useState<Mode>('mobile');
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);

  const searching = fetcher.state !== 'idle';
  const response = fetcher.data;

  const invalid =
    mode === 'mobile'
      ? !MOBILE_PATTERN.test(value)
        ? 'Утасны дугаар 8 оронтой байна.'
        : null
      : !EMAIL_PATTERN.test(value.trim())
        ? 'И-мэйл хаяг буруу байна.'
        : null;

  const switchMode = (next: Mode) => {
    setMode(next);
    setValue('');
    setTouched(false);
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (invalid) return;
    fetcher.submit({ mode, value: value.trim() }, { method: 'post', encType: 'application/json' });
  };

  return (
    <>
      <form
        onSubmit={onSubmit}
        noValidate
        className="animate-rise rounded-3xl bg-white p-5 shadow-xl sm:p-8"
      >
        <h2 className="text-xl font-bold text-green-900">Бүртгэл шалгах</h2>
        <p className="mt-1 text-sm text-green-800 text-opacity-90">
          Бүртгүүлсэн утасны дугаар эсвэл и-мэйл хаягаараа хайна уу.
        </p>

        <div role="tablist" className="mt-6 grid grid-cols-2 gap-1 rounded-2xl bg-green-50 p-1">
          {(
            [
              ['mobile', 'Утасны дугаар'],
              ['email', 'И-мэйл'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={mode === key}
              onClick={() => switchMode(key)}
              className={clsx(
                'rounded-xl py-3 text-sm font-semibold duration-200 ease-in-out sm:text-base',
                mode === key
                  ? 'bg-white text-green-700 shadow'
                  : 'text-green-800 hover:text-green-600',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <label
          htmlFor="lookup-value"
          className="ml-0.5 mt-6 block text-sm font-medium text-green-900"
        >
          {mode === 'mobile' ? 'Утасны дугаар' : 'И-мэйл хаяг'}
        </label>
        <input
          id="lookup-value"
          key={mode}
          type={mode === 'mobile' ? 'tel' : 'email'}
          inputMode={mode === 'mobile' ? 'numeric' : 'email'}
          autoComplete={mode === 'mobile' ? 'tel' : 'email'}
          maxLength={mode === 'mobile' ? 8 : 254}
          placeholder={mode === 'mobile' ? '99112233' : 'name@example.com'}
          value={value}
          onChange={(e) =>
            setValue(mode === 'mobile' ? e.target.value.replace(/\D/g, '') : e.target.value)
          }
          onBlur={() => value && setTouched(true)}
          aria-invalid={touched && !!invalid}
          className={clsx(
            'mt-2 h-14 w-full rounded-2xl border-2 p-4 text-base font-medium text-green-900 placeholder-green-700 placeholder-opacity-50 outline-none duration-300 ease-in-out focus:outline-none',
            touched && invalid
              ? 'border-red-300 focus:border-red-400 focus:ring-red-200'
              : 'border-green-50 focus:border-green-200 focus:ring-green-200',
          )}
        />
        {touched && invalid && <p className="mt-1.5 text-sm text-red-500">{invalid}</p>}

        <Button type="submit" className="mt-6 w-full" disabled={searching}>
          {searching ? 'Хайж байна…' : 'Хайх'}
        </Button>
      </form>

      <div className="mt-8" aria-live="polite">
        {response && !response.ok && (
          <div className="rounded-2xl bg-red-50 px-5 py-4 text-sm font-medium text-red-700">
            {response.error}
          </div>
        )}
        {response?.ok &&
          (response.result.participants.length > 0 ? (
            <>
              <p className="mb-4 text-green-800">
                {response.result.participants.length} оролцогч олдлоо.
              </p>
              <div className="space-y-5">
                {response.result.participants.map((participant, index) => (
                  <ResultCard
                    key={`${participant.first_name}-${index}`}
                    participant={participant}
                    competition={response.result.competition}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="rounded-3xl bg-white p-6 text-center shadow-lg sm:p-8">
              <p className="font-semibold text-green-900">Оролцогч олдсонгүй.</p>
              <p className="mt-1 text-sm text-green-800">
                Бүртгүүлэхдээ өгсөн {mode === 'mobile' ? 'утасны дугаараа' : 'и-мэйлээ'} шалгаад
                дахин оролдоно уу.
              </p>
            </div>
          ))}
      </div>
    </>
  );
}

export default function CompetitionPage({
  competition,
  results,
}: {
  competition: Competition;
  results: Results | null;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = STATUS_LABELS[competition.status];
  const registerReason = unavailableReason('register', competition);
  const checkReason = unavailableReason('check', competition);

  // ?tab=register|check when it can be opened, else registration while it is
  // open (the default), else the lookup.
  const requested = searchParams.get('tab');
  const panel: Panel | null =
    requested === 'register' && !registerReason
      ? 'register'
      : requested === 'check' && !checkReason
        ? 'check'
        : !registerReason
          ? 'register'
          : !checkReason
            ? 'check'
            : null;

  const select = (next: Panel) =>
    setSearchParams({ tab: next }, { replace: true, preventScrollReset: true });

  return (
    <section className="min-h-[70vh] bg-gradient-to-b from-green-25 to-green-50 px-4 pb-20 pt-8 sm:px-6 lg:px-8 lg:pt-16">
      <div className="mx-auto max-w-3xl">
        <Link
          to="/competitions"
          className="inline-flex items-center gap-1 text-sm font-medium text-green-700 hover:text-green-600"
        >
          <Icon icon="chevronRight" className="h-4 w-4 rotate-180" />
          Тэмцээн, уралдаан
        </Link>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-green-600 px-3 py-1 text-xs font-semibold text-white">
            {TYPE_LABELS[competition.type]}
          </span>
          {status && (
            <span
              className={clsx('rounded-full px-3 py-1 text-xs font-semibold', status.className)}
            >
              {status.label}
            </span>
          )}
        </div>
        <h1 className="mt-3 text-3xl font-bold leading-tight text-green-900 sm:text-4xl">
          {competition.name}
        </h1>
        <p className="mt-2 text-green-800">
          {[formatDateRange(competition.start_date, competition.end_date), competition.location]
            .filter(Boolean)
            .join(' · ')}
        </p>

        {competition.purpose && (
          <div className="mt-6 rounded-3xl bg-white p-5 shadow-lg sm:p-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-green-600">Зорилго</p>
            <p className="mt-2 whitespace-pre-line leading-relaxed text-green-900">
              {competition.purpose}
            </p>
          </div>
        )}
        {competition.description && <Description html={competition.description} />}

        <div role="tablist" className="mt-10 grid gap-4 sm:grid-cols-2">
          <PanelButton
            icon="send"
            title="Бүртгүүлэх"
            subtitle="Тэмцээнд оролцохоор бүртгүүлэх"
            selected={panel === 'register'}
            reason={registerReason}
            onClick={() => select('register')}
          />
          <PanelButton
            icon="listCheck"
            title="Бүртгэл шалгах"
            subtitle="Өрөө, цаг, шалгалтын дугаараа харах"
            selected={panel === 'check'}
            reason={checkReason}
            onClick={() => select('check')}
          />
        </div>

        <div className="mt-6">
          {panel === 'register' && competition.forms_slug && (
            <RegisterPanel slug={competition.forms_slug} />
          )}
          {panel === 'check' && <CheckPanel />}
        </div>

        {results && results.class_years.length > 0 && <ResultsSection results={results} />}

        <TipsList tips={competition.tips} className="mt-8" />
      </div>
    </section>
  );
}
